<template>
  <div>
    <!-- Vehicle Challan List Section Start -->
    <div>
      <b-button
        v-if="showTriggerButton"
        class="mb-1 mt-1"
        @click="showChallanModal"
        size="xs"
        style="font-size: 0.675rem"
        :variant="triggerButtonVariant"
      >
        {{ triggerButtonText }}
      </b-button>
    </div>
    <!-- Inline Display Content -->
    <div v-if="inlineDisplay">
      <CustomLoader v-if="isLoading" :width="70"></CustomLoader>
      <div v-if="!isLoading">
        <b-row>
          <b-col md="12">
            <!-- Excel Export Button -->
            <b-button
              class="pull-right mb-1"
              v-if="challanList.length > 0"
              :disabled="isExporting"
              @click="exportToExcel"
              size="sm"
              variant="warning"
            >
              {{ exportButtonText }}
              <b-spinner
                v-if="isExporting"
                small
                label="Exporting..."
              ></b-spinner>
            </b-button>
            
            <!-- Refresh Button -->
            <b-button
              class="pull-right mb-1 mr-2"
              @click="refreshData"
              size="sm"
              variant="info"
              :disabled="isLoading"
            >
              <md-icon icon="refresh" aria-hidden="true">refresh</md-icon>
              Refresh
            </b-button>
            
            <!-- Search Filter -->
            <b-form-input
              v-model="filter"
              type="search"
              placeholder="Search challans..."
              class="mb-2"
            ></b-form-input>
              <!-- Summary Information -->
             <div v-if="challanList.length > 0" class="mt-3">
               <b-card class="bg-light">
                 <b-row>
                   <b-col md="2">
                     <small class="text-muted">Total Challans:</small>
                     <div class="h6">{{ totalRows }}</div>
                   </b-col>
                   <b-col md="2">
                     <small class="text-muted">Disposed:</small>
                     <div class="h6 text-success">{{ disposedCount }}</div>
                     <small class="text-success">₹{{ disposedFineAmount }}</small>
                   </b-col>
                   <b-col md="2">
                     <small class="text-muted">Pending:</small>
                     <div class="h6 text-warning">{{ pendingCount }}</div>
                     <small class="text-warning">₹{{ pendingFineAmount }}</small>
                   </b-col>
                   <b-col md="3">
                     <small class="text-muted">Total Fine:</small>
                     <div class="h6 text-info">₹{{ totalFineAmount }}</div>
                   </b-col>
                   <b-col md="3">
                     <small class="text-muted">Outstanding:</small>
                     <div class="h6 text-danger">₹{{ pendingFineAmount }}</div>
                   </b-col>
                 </b-row>
               </b-card>
             </div>
            <!-- Challan Table -->
            <b-table
              show-empty
              stacked="md"
              responsive
              :items="challanList"
              :fields="challanFields"
              :per-page="perPage"
              :current-page="currentPage"
              :filter="filter"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              :sort-direction="sortDirection"
              @filtered="onFiltered"
              class="position-relative"
            >
              <template slot="SN" slot-scope="row0">
                <span>{{ row0.index + 1 + (currentPage - 1) * perPage }}</span>
              </template>
              
              <template slot="Status" slot-scope="row">
                <b-badge
                  :variant="getStatusVariant(row.item.Status)"
                  :class="getStatusClass(row.item.Status)"
                >
                  {{ row.item.Status }}
                </b-badge>
              </template>
              
              <template slot="Fine Imposed" slot-scope="row">
                <span v-if="row.item['Fine Imposed'] && row.item['Fine Imposed'] !== 'N/A'">
                  ₹{{ row.item['Fine Imposed'] }}
                </span>
                <span v-else class="text-muted">N/A</span>
              </template>
              
              <template slot="Received Amount" slot-scope="row">
                <span v-if="row.item['Received Amount'] && row.item['Received Amount'] !== 'N/A'">
                  ₹{{ row.item['Received Amount'] }}
                </span>
                <span v-else class="text-muted">N/A</span>
              </template>
              
              <template slot="Challan Date/Time" slot-scope="row">
                <span>{{ (row.item['Challan Date/Time']) }}</span>
              </template>
              
              <template slot="actions" slot-scope="row">
                <b-button
                  size="sm"
                  variant="outline-primary"
                  @click="viewChallanDetails(row.item)"
                  class="mr-1"
                >
                  <md-icon icon="visibility" aria-hidden="true">visibility</md-icon>
                  View
                </b-button>
              </template>
            </b-table>
            
            <!-- Pagination -->
            <div v-if="totalRows > perPage">
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                align="center"
                size="sm"
                class="my-0"
              ></b-pagination>
            </div>
            
           
          </b-col>
        </b-row>
      </div>
    </div>
    
    <!-- Challan Details Modal -->
    <b-modal
      ref="details-modal"
      :title="`Challan Details - ${selectedChallan ? selectedChallan['Challan No'] : 'N/A'}`"
      size="lg"
      hide-footer
    >
      <div v-if="selectedChallan">
        <b-row>
          <b-col md="6" v-for="(value, key) in selectedChallan" :key="key">
            <div class="mb-2">
              <strong>{{ formatFieldName(key) }}:</strong>
              <div class="text-muted">{{ value || 'N/A' }}</div>
            </div>
          </b-col>
        </b-row>
      </div>
      <template #modal-footer>
        <b-button variant="secondary" @click="hideDetailsModal">Close</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import UlipService from '../../services/UlipService.js';
import CustomLoader from './customLoader.vue';
import { DateFormat } from '../../helper/filters';
import * as XLSX from 'xlsx';

export default {
  name: 'VehicleChallanList',
  components: {
    CustomLoader
  },
  props: {
    // Modal trigger button props
    showTriggerButton: {
      type: Boolean,
      default: true
    },
    triggerButtonText: {
      type: String,
      default: 'View Challans'
    },
    triggerButtonVariant: {
      type: String,
      default: 'primary'
    },
    modalTitle: {
      type: String,
      default: 'Vehicle Challan List'
    },
    // API payload for fetching data
    apiPayload: {
      type: Object,
      default: function() {
        return {};
      }
    },
    // Auto-load data on mount
    autoLoad: {
      type: Boolean,
      default: false
    },
    // Pre-loaded challan data (optional)
    challanData: {
      type: Array,
      default: function() {
        return [];
      }
    },
    // Show data inline instead of in modal
    inlineDisplay: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      challanList: [],
      selectedChallan: null,
      isLoading: false,
      isExporting: false,
      exportButtonText: 'Export As Excel',
      
      // Table configuration
      totalRows: 0,
      currentPage: 1,
      perPage: 10,
      pageOptions: [5, 10, 15, 20, 25],
      sortBy: 'Challan Date/Time',
      sortDesc: true,
      sortDirection: 'desc',
      filter: null,
      
      // Table fields configuration
      challanFields: [
        {
          key: 'SN',
          label: 'S.No',
          sortable: false,
          thClass: 'text-center',
          tdClass: 'text-center'
        },
        {
          key: 'Challan No',
          label: 'Challan No',
          sortable: true,
          thClass: 'text-center'
        },
        {
          key: 'Challan Date/Time',
          label: 'Date/Time',
          sortable: true,
          thClass: 'text-center'
        },
        {
          key: 'Challan Place',
          label: 'Place',
          sortable: true,
          thClass: 'text-center'
        },
        {
          key: 'Status',
          label: 'Status',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center'
        },
        {
          key: 'Vehicle No',
          label: 'Vehicle No',
          sortable: true,
          thClass: 'text-center'
        },
        {
          key: 'Driver Name',
          label: 'Driver',
          sortable: true,
          thClass: 'text-center'
        },
        {
          key: 'Fine Imposed',
          label: 'Fine Amount',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-right'
        },
        {
          key: 'actions',
          label: 'Actions',
          sortable: false,
          thClass: 'text-center',
          tdClass: 'text-center'
        }
      ]
    };
  },
  computed: {
    disposedCount() {
      var self = this;
      var count = this.challanList.filter(function(item) {
        return item.Status && item.Status.toLowerCase() === 'disposed';
      }).length;
      console.log('Disposed count:', count, 'from', this.challanList.length, 'total challans');
      return count;
    },
    pendingCount() {
      var self = this;
      var count = this.challanList.filter(function(item) {
        return item.Status && item.Status.toLowerCase() === 'pending';
      }).length;
      console.log('Pending count:', count, 'from', this.challanList.length, 'total challans');
      return count;
    },
    totalFineAmount() {
      var self = this;
      return this.challanList.reduce(function(total, item) {
        var fine = parseFloat(item['Fine Imposed']) || 0;
        return total + fine;
      }, 0);
    },
    disposedFineAmount() {
      var self = this;
      return this.challanList.reduce(function(total, item) {
        if (item.Status && item.Status.toLowerCase() === 'disposed') {
          var fine = parseFloat(item['Fine Imposed']) || 0;
          return total + fine;
        }
        return total;
      }, 0);
    },
    pendingFineAmount() {
      var self = this;
      return this.challanList.reduce(function(total, item) {
        if (item.Status && item.Status.toLowerCase() === 'pending') {
          var fine = parseFloat(item['Fine Imposed']) || 0;
          return total + fine;
        }
        return total;
      }, 0);
    }
  },
  mounted() {
    // Use pre-loaded data if available
    if (this.challanData && this.challanData.length > 0) {
      this.challanList = this.challanData;
      this.totalRows = this.challanList.length;
    } else {
      this.loadChallanData();
      this.loadDefaultChallanData();
    }
  },
  watch: {
    challanData: {
      handler: function(newData) {
        console.log('VehicleChallanList - challanData changed:', newData);
        if (newData && newData.length > 0) {
          this.challanList = newData;
          this.totalRows = this.challanList.length;
          console.log('VehicleChallanList - challanList updated:', this.challanList);
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    showChallanModal() {
      this.$refs['challan-modal'].show();
      if (!this.apiPayload || Object.keys(this.apiPayload).length === 0) {
        this.loadDefaultChallanData();
      } else {
        this.loadChallanData();
      }
    },
    
    hideChallanModal() {
      this.$refs['challan-modal'].hide();
      this.challanList = [];
      this.filter = null;
    },
    
    viewChallanDetails(challan) {
      this.selectedChallan = challan;
      var self = this;
      this.$nextTick(function() {
        self.$refs['details-modal'].show();
      });
    },
    
    hideDetailsModal() {
      this.$refs['details-modal'].hide();
      this.selectedChallan = null;
    },
    
    async loadChallanData() {
      this.isLoading = true;
      try {
        const response = await UlipService.getVehicleChallanExcelData(this.apiPayload);
        const { data, status } = response;
        
        if (status === 200 && data && data.type === 'success') {
          this.challanList = data.data || [];
          this.totalRows = this.challanList.length;
        } else {
          this.challanList = [];
          this.totalRows = 0;
          this.$toast.error(data.message || 'Failed to load challan data');
        }
      } catch (error) {
        console.error('Error loading challan data:', error);
        this.challanList = [];
        this.totalRows = 0;
        this.$toast.error('Error loading challan data');
      } finally {
        this.isLoading = false;
      }
    },

    async loadDefaultChallanData() {
      this.isLoading = true;
      try {
        // Use the same approach as ECHALLAN_ResponseDisplay to get default challan data
        const payload = {
          itemCode: "ECHALLAN01",
          funcUrl: "ECHALLAN/01",
          vehiclenumber: "ALL" // Load all vehicles by default
        };

        const response = await UlipService.getAllUlipList(JSON.stringify(payload));
        const { data, status } = response;
        
        if (status === 200 && data && data.xmlJson) {
          const challanData = data.xmlJson;
          
          // Combine pending and disposed data if they exist separately
          let allChallans = [];
          if (challanData.data) {
            if (challanData.data.Pending_data) {
              allChallans = allChallans.concat(challanData.data.Pending_data);
            }
            if (challanData.data.Disposed_data) {
              allChallans = allChallans.concat(challanData.data.Disposed_data);
            }
          }
          
          // Transform the data to match the expected format
          this.challanList = allChallans.map((challan, index) => ({
            'S.No': index + 1,
            'Vehicle No': challan.vehicleNo,
            'Challan No': challan.challan_no,
            'Challan Date/Time': challan.challan_date_time,
            'Challan Place': challan.challan_place,
            'Status': challan.challan_status,
            'Sent to Court': challan.sent_to_reg_court,
            'Remark': challan.remark,
            'Fine Imposed': challan.fine_imposed,
            'DL No': challan.dl_no,
            'Driver Name': challan.driver_name,
            'Owner Name': challan.owner_name,
            'Violator Name': challan.name_of_violator,
            'Receipt No': challan.receipt_no || 'N/A',
            'Received Amount': challan.received_amount || 'N/A',
            'Department': challan.department,
            'State Code': challan.state_code,
            'Document Impounded': challan.document_impounded || 'N/A',
            'Offence Details': challan.offence_details_names || 'N/A',
            'Amount of Fine Imposed': challan.amount_of_fine_imposed || 'N/A',
            'Court Address': challan.court_address || 'N/A',
            'Court Name': challan.court_name || 'N/A',
            'Date of Proceeding': challan.date_of_proceeding || 'N/A',
            'Sent to Court On': challan.sent_to_court_on || 'N/A',
            'Sent to Virtual Court': challan.sent_to_virtual_court,
            'RTO District': challan.rto_distric_name || 'N/A',
            'Status Type': challan.status_type,
          }));
          
          this.totalRows = this.challanList.length;
        } else {
          this.challanList = [];
          this.totalRows = 0;
        }
      } catch (error) {
        console.error('Error loading default challan data:', error);
        this.challanList = [];
        this.totalRows = 0;
      } finally {
        this.isLoading = false;
      }
    },
    
    async refreshData() {
      if (!this.apiPayload || Object.keys(this.apiPayload).length === 0) {
        await this.loadDefaultChallanData();
      } else {
        await this.loadChallanData();
      }
    },
    
    async exportToExcel() {
      this.isExporting = true;
      this.exportButtonText = 'Downloading...';
      
      try {
        if (this.challanList.length === 0) {
          alert('No challan data available to export');
          return;
        }

        console.log('Exporting challan data:', this.challanList);

        // Generate Excel file from the current data
        this.generateExcelFromData(this.challanList);

      } catch (error) {
        console.error('Error exporting to Excel:', error);
        alert('Error exporting to Excel. Please try again.');
      } finally {
        this.isExporting = false;
        this.exportButtonText = 'Export As Excel';
      }
    },

    generateExcelFromData(challanData) {
      try {
        // Prepare data for Excel
        const excelData = challanData.map((challan, index) => ({
          'S.No': index + 1,
          'Challan No': challan['Challan No'],
          'Challan Date/Time': challan['Challan Date/Time'],
          'Challan Place': challan['Challan Place'],
          'Status': challan['Status'],
          'Sent to Court': challan['Sent to Court'],
          'Remark': challan['Remark'],
          'Fine Imposed': challan['Fine Imposed'],
          'DL No': challan['DL No'],
          'Driver Name': challan['Driver Name'],
          'Owner Name': challan['Owner Name'],
          'Violator Name': challan['Violator Name'],
          'Receipt No': challan['Receipt No'] || 'N/A',
          'Received Amount': challan['Received Amount'] || 'N/A',
          'Department': challan['Department'],
          'State Code': challan['State Code'],
          'Document Impounded': challan['Document Impounded'] || 'N/A',
          'Offence Details': challan['Offence Details'] || 'N/A',
          'Amount of Fine Imposed': challan['Amount of Fine Imposed'] || 'N/A',
          'Court Address': challan['Court Address'] || 'N/A',
          'Court Name': challan['Court Name'] || 'N/A',
          'Date of Proceeding': challan['Date of Proceeding'] || 'N/A',
          'Sent to Court On': challan['Sent to Court On'] || 'N/A',
          'Sent to Virtual Court': challan['Sent to Virtual Court'],
          'RTO District': challan['RTO District'] || 'N/A',
          'Status Type': challan['Status Type'],
          'Vehicle No': challan['Vehicle No']
        }));

        // Create workbook and worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(excelData);

        // Set column widths for better readability
        const columnWidths = [
          { wch: 8 },   // S.No
          { wch: 25 },  // Challan No
          { wch: 20 },  // Challan Date/Time
          { wch: 50 },  // Challan Place
          { wch: 12 },  // Status
          { wch: 15 },  // Sent to Court
          { wch: 15 },  // Remark
          { wch: 15 },  // Fine Imposed
          { wch: 20 },  // DL No
          { wch: 20 },  // Driver Name
          { wch: 25 },  // Owner Name
          { wch: 25 },  // Violator Name
          { wch: 20 },  // Receipt No
          { wch: 18 },  // Received Amount
          { wch: 15 },  // Department
          { wch: 12 },  // State Code
          { wch: 20 },  // Document Impounded
          { wch: 60 },  // Offence Details
          { wch: 20 },  // Amount of Fine Imposed
          { wch: 30 },  // Court Address
          { wch: 25 },  // Court Name
          { wch: 20 },  // Date of Proceeding
          { wch: 20 },  // Sent to Court On
          { wch: 20 },  // Sent to Virtual Court
          { wch: 20 },  // RTO District
          { wch: 15 },  // Status Type
          { wch: 15 }   // Vehicle No
        ];
        worksheet['!cols'] = columnWidths;

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Challan Data');

        // Generate filename with timestamp
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        const filename = `Vehicle_Challans_${timestamp}.xlsx`;

        // Save file
        XLSX.writeFile(workbook, filename);

        console.log(`Excel file generated successfully: ${filename}`);

      } catch (error) {
        console.error('Error generating Excel file:', error);
        alert('Error generating Excel file. Please try again.');
      }
    },
    
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    
    getStatusVariant(status) {
      if (!status) return 'secondary';
      const statusLower = status.toLowerCase();
      if (statusLower === 'disposed') return 'success';
      if (statusLower === 'pending') return 'warning';
      return 'secondary';
    },
    
    getStatusClass(status) {
      if (!status) return '';
      const statusLower = status.toLowerCase();
      if (statusLower === 'disposed') return 'text-white';
      if (statusLower === 'pending') return 'text-dark';
      return 'text-white';
    },
    
    formatDateTime(dateTime) {
      if (!dateTime) return 'N/A';
      return DateFormat(dateTime);
    },
    
    formatFieldName(fieldName) {
      return fieldName.replace(/([A-Z])/g, ' $1')
        .replace(/^./, function(str) {
          return str.toUpperCase();
        })
        .trim();
    }
  }
};
</script>

<style scoped>
.table th {
  background-color: #f8f9fa;
  border-top: none;
}

.table td {
  vertical-align: middle;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.text-muted {
  color: #6c757d !important;
}

.h6 {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0;
}
</style>



// WEBPACK FOOTER //
// VehicleChallanList.vue?ebbe10de